import {useEffect, useState} from 'react';
import {Upload, Button, Row, Col, Typography, Skeleton, Card, Empty, message, Divider} from 'antd';
import {PlusOutlined, UploadOutlined} from '@ant-design/icons';
import * as API_BOOKS from '../../../../shared/http/books';
import {Book} from '../../../../shared/types/api';
import BookCard from "./ui/BookCard";
import CatalogBookCard from "./ui/CatalogBookCard";
import {useAppDispatch, useAppSelector} from "../../../../store/hooks";
import {fetchBooks} from "../../../../store/books/extraReducers";
import {
    booksDataSelector,
    booksIsLoadedSelector,
    booksIsLoadingSelector,
    setBooksRoutine
} from "../../../../store/books";
import {Helmet} from "react-helmet";

const {Title} = Typography;

const BooksLibrary = () => {

    const dispatch = useAppDispatch()

    const [uploading, setUploading] = useState<boolean>(false);
    const [catalogBooks, setCatalogBooks] = useState<Book[]>([]);
    const [catalogLoading, setCatalogLoading] = useState(false);

    const getBooks = () => dispatch(fetchBooks())

    const books = useAppSelector(booksDataSelector)
    const isBooksLoaded = useAppSelector(booksIsLoadedSelector)
    const loading = useAppSelector(booksIsLoadingSelector)

    useEffect(() => {
        if (!isBooksLoaded) {
            getBooks()
        }
        setCatalogLoading(true);
        API_BOOKS.getCatalogBooks()
            .then((data) => setCatalogBooks(data || []))
            .catch(() => {})
            .finally(() => setCatalogLoading(false));
    }, []);

    const uploadFile = async (info: any) => {
        setUploading(true);
        try {
            const newBook = await API_BOOKS.uploadBook(info.file);
            dispatch(setBooksRoutine([...books, newBook.data]))
            message.success('Книга успешно загружена');
        } catch (error) {
            message.error('Ошибка при загрузке книги');
            console.error(error);
        } finally {
            setUploading(false);
        }
    };

    const deleteBook = (id: number) => {
        const filtered = [...books].filter(b => b.id !== id);
        dispatch(setBooksRoutine(filtered))
    }

    const addToLibrary = async (catalogBook: Book) => {
        try {
            await API_BOOKS.addCatalogBookToLibrary(catalogBook.id);
            await getBooks();
            message.success(`«${catalogBook.title}» добавлена в библиотеку`);
        } catch (e) {
            message.error('Ошибка при добавлении книги');
        }
    }

    return (
        <div style={{padding: 24}}>
            <Helmet title='Книги'
                    meta={[
                        {
                            'name': 'description',
                            'content': 'Изучайте новые слова читая любимые книги'
                        },
                        {'property': 'og:type', 'content': 'product.group'}
                    ]}/>
            <Title level={2}>Моя библиотека</Title>

            <Upload
                name="file"
                accept=".epub"
                showUploadList={false}
                customRequest={uploadFile}
                disabled={uploading}
            >
                <Button icon={<UploadOutlined/>} loading={uploading} disabled={uploading}>
                    {uploading ? 'Загрузка...' : 'Загрузить книгу (.epub)'}
                </Button>
            </Upload>

            <div style={{marginTop: 24}}>
                {loading ? (
                    <Row gutter={[16, 16]}>
                        {[...Array(2)].map((_, idx) => (
                            <Col key={idx}>
                                <Card style={{width: 300, margin: 16}}>
                                    <Skeleton active title paragraph={{rows: 4}}/>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                ) : books.length === 0 ? (
                    <Empty description="Нет загруженных книг"/>
                ) : (
                    <Row gutter={[16, 16]}>
                        {books.map((book: Book) => (
                            <Col key={book.id}>
                                <BookCard book={book} deleteBook={deleteBook}/>
                            </Col>
                        ))}
                    </Row>
                )}
            </div>

            {catalogBooks.length > 0 && (
                <>
                    <Divider/>
                    <Title level={2}>Каталог книг</Title>
                    <div style={{marginTop: 16}}>
                        {catalogLoading ? (
                            <Row gutter={[16, 16]}>
                                {[...Array(2)].map((_, idx) => (
                                    <Col key={idx}>
                                        <Card style={{width: 300, margin: 16}}>
                                            <Skeleton active title paragraph={{rows: 4}}/>
                                        </Card>
                                    </Col>
                                ))}
                            </Row>
                        ) : (
                            <Row gutter={[16, 16]}>
                                {catalogBooks.map((book: Book) => (
                                    <Col key={book.id}>
                                        <CatalogBookCard book={book} addToLibrary={addToLibrary}/>
                                    </Col>
                                ))}
                            </Row>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default BooksLibrary;
